import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { announceRequests, assignAccount, claimAccount, panelMessage, postIntake } from '../api/client';
import type { TokenGetter } from '../api/client';
import type { BookAccount, StrategistOption, WriteActionResult } from '../api/types';
import { writeOutcomeLine } from '../lib/writeOutcome';
import { matchingAccount } from './bankRequest';
import { buildCsv } from './exportCsv';
import { IntakeAssignmentCell } from './IntakeAssignmentCell';
import { intakeBlock } from './intakeBlocks';
import {
  ASSIGN_ON_UPLOAD_REASON, BANK_OWNER_VALUE, intakeOwnerChoices, netNewOwnerChoices,
  plannedRowOwner,
  shouldAssignAfterIntake,
  shouldClaimAfterIntake, claimAccountIdFromDoor,
} from './intakeOwner';
import type { IntakeMode, PlannedOwner } from './intakeOwner';
import {
  INTAKE_RESULT_CSV_COLUMNS, assignedLine, bankResultLabel, isAssigned, isRequested,
} from './intakeResults';
import type { IntakeResultRow } from './intakeResults';
import { parseIntakeCsv } from './parseCsv';
import type { ParsedIntakeRow } from './parseCsv';
import { strategistLabel } from './queueSignal';
import {
  MAX_REQUEST_REASON_LENGTH, REQUEST_REASON_LABEL, REQUEST_REASON_PLACEHOLDER, requestReason,
  requestedRefs, rowRequestReason,
} from './requestReason';

// The typed single row has no evidence input; the door still needs one for its
// 24h duplicate check and its audit line, so it gets the same constant a CSV does.
const TYPED_ROW_EVIDENCE = 'CSV upload';

// Everything that reads differently when a strategist uploads. The steps, the preview
// and the results table are shared; only the verb, and who decides, change.
const MODE_COPY = {
  assign: {
    title: 'Upload and assign a list',
    intro: 'Add several accounts to the bank and assign each one to a strategist in one step. '
      + 'Accounts someone already owns keep their owner.',
    netNewTitle: 'Add a net new account',
    netNewIntro: 'A company that is not on the bank yet. It is assigned in the same step.',
    ownerStep: 'Default owner',
    ownerHint: 'Rows with no owner in the CSV go to this strategist. Leave it unset and '
      + 'those rows stay in the bank.',
    netNewOwnerStep: 'Assign to',
    netNewOwnerHint: 'Who owns this account after it is added.',
    ownerColumnHint: "A strategist's email or first name. Overrides the default owner for "
      + 'that row.',
    verbPast: 'assigned',
    preposition: 'to',
    verbButton: 'Assign',
    resultColumn: 'Assignment',
    problemFate: "they'll be added to the bank unassigned",
  },
  request: {
    title: 'Upload and request a list',
    intro: 'Add several accounts to the bank and request each one for a strategist. Leadership '
      + 'approves every request before anything is assigned. Accounts someone already owns '
      + 'keep their owner.',
    netNewTitle: 'Request a net new account',
    netNewIntro: 'A company that is not on the bank yet. Leadership reviews it like any other '
      + 'request.',
    ownerStep: 'Request for',
    ownerHint: 'Rows with no owner in the CSV are requested for this strategist. Leave it '
      + 'unset and those rows stay in the bank with no request.',
    netNewOwnerStep: 'Request for',
    netNewOwnerHint: 'Whose request this is. Leadership still approves it.',
    ownerColumnHint: "A strategist's email or first name. Requests that row for them instead.",
    verbPast: 'requested',
    preposition: 'for',
    verbButton: 'Request',
    resultColumn: 'Request',
    problemFate: "they'll be added to the bank with no request",
  },
};

interface PlannedRow {
  row: ParsedIntakeRow;
  owner: PlannedOwner;
}

// What the owner step adds to a row: the write's outcome, and the gate's reasons if it blocked.
type RowAssignment = Pick<
  IntakeResultRow,
  'assignStatus' | 'assignReason' | 'assignOwner' | 'assignMessage' | 'exclusion_reason'
  | 'exclusion_detail' | 'reasons' | 'forceable' | 'forced'
>;

interface UploadPlan {
  filename: string;
  rows: ParsedIntakeRow[];
}

function downloadCsvFile(filename: string, csvText: string) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function templateCsv(strategists: StrategistOption[]): string {
  const exampleOwner = strategists[0]?.email || 'strategist@cartesia.ai';
  return 'name,domain,owner\n'
    + `Acme Corp,acme.com,${exampleOwner}\n`
    + 'Globex,globex.com,\n';
}

function planRows(
  rows: ParsedIntakeRow[],
  defaultOwner: string,
  strategists: StrategistOption[],
): PlannedRow[] {
  const planned: PlannedRow[] = [];
  for (const row of rows) {
    const owner = plannedRowOwner({ csvOwner: row.owner, defaultOwner, strategists });
    planned.push({ row, owner });
  }
  return planned;
}

export function CsvIntake({
  getToken,
  mode,
  viewerEmail,
  accounts,
  strategists,
  strategistsLoading,
  viewAsRole,
  onOwnership,
}: {
  getToken: TokenGetter;
  // From the server's `intake_mode`: an approver assigns, a strategist requests.
  mode: IntakeMode;
  viewerEmail: string;
  accounts: BookAccount[];
  strategists: StrategistOption[];
  strategistsLoading: boolean;
  // Set when an admin is viewing as someone else: the panel renders as they see it,
  // but every write would be refused, so the inputs are off.
  viewAsRole: string | null;
  onOwnership: (answer: WriteActionResult) => void;
}) {
  const copy = MODE_COPY[mode];
  const [panelOpen, setPanelOpen] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  // A strategist most often uploads a list for themselves, so that is their default.
  const [defaultOwner, setDefaultOwner] = useState(
    mode === 'request' ? viewerEmail : BANK_OWNER_VALUE,
  );
  const [singleOwner, setSingleOwner] = useState(
    mode === 'request' ? viewerEmail : BANK_OWNER_VALUE,
  );
  const [plan, setPlan] = useState<UploadPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errorText, setErrorText] = useState('');
  const [results, setResults] = useState<IntakeResultRow[]>([]);
  const [existingMatch, setExistingMatch] = useState<BookAccount | null>(null);
  // A strategist's "why" for the accounts they request here, sent with every row's request.
  const [why, setWhy] = useState('');
  const missingWhy = mode === 'request' && requestReason(why) === null;

  const previewOnly = Boolean(viewAsRole);
  const locked = busy || previewOnly;
  const ownerChoices = intakeOwnerChoices(strategists);
  const singleOwnerChoices = netNewOwnerChoices(strategists);
  const defaultOwnerLabel = defaultOwner
    ? strategistLabel(defaultOwner, strategists)
    : '';
  const singleOwnerLabel = singleOwner
    ? strategistLabel(singleOwner, strategists)
    : '';

  // Step two of each row. An approver goes through assign_account, the sole writer of
  // SDR_Owner__c. A strategist's row becomes a request, which nothing writes until an
  // approver says yes; the evidence travels with it so the queue shows where it came from.
  async function ownRow(
    accountName: string,
    accountDomain: string,
    ownerEmail: string,
    evidence: string,
    accountId?: string | null,
    overrideExclusion = false,
    requested?: WriteActionResult[],
  ): Promise<RowAssignment> {
    try {
      // A request carries the list's "why" (plus the row's own evidence), and waits for the
      // upload's one Slack announcement at the end rather than posting a message per row.
      const answer = mode === 'assign'
        ? await assignAccount(getToken, {
          domain: accountDomain,
          owner: ownerEmail,
          reason: ASSIGN_ON_UPLOAD_REASON,
          account_id: accountId || undefined,
          override_exclusion: overrideExclusion || undefined,
        })
        : await claimAccount(getToken, {
          domain: accountDomain,
          owner: ownerEmail,
          reason: rowRequestReason(why, evidence, TYPED_ROW_EVIDENCE) ?? '',
          evidence,
          name: accountName,
          account_id: accountId || undefined,
          announce: false,
        });
      if (mode === 'request' && requested) requested.push(answer);
      onOwnership({ ...answer, name: answer.name || accountName });

      // An approver's write reads as the Slack lines do: the owner's name and what it was forced
      // over. A request needs no sentence; anything else explains itself.
      const assigned = mode === 'assign' ? assignedLine(answer, ownerEmail) : null;
      const settled = answer.outcome === 'written' || answer.outcome === 'requested';
      return {
        assignStatus: answer.outcome,
        assignReason: answer.reason || undefined,
        assignOwner: answer.owner || ownerEmail,
        assignMessage: assigned ?? (settled ? '' : writeOutcomeLine(answer, copy.verbButton)),
        exclusion_reason: answer.exclusion_reason || undefined,
        exclusion_detail: answer.exclusion_detail || undefined,
        // The write's reasons are the later word: they replace whatever the door said.
        reasons: answer.reasons || [],
        forceable: answer.forceable ?? undefined,
        forced: answer.forced === true,
      };
    } catch (error) {
      return {
        assignStatus: 'refused',
        assignOwner: ownerEmail,
        assignMessage: panelMessage(error, `${copy.verbButton} failed.`),
      };
    }
  }

  // "Assign anyway" on one blocked row: the same write, forced, and the row redrawn in place.
  // `assign_account` checks the gate again, so a never-contact row still refuses.
  async function forceRow(index: number) {
    const row = results[index];
    if (!row?.targetOwner || !row.account_id) return;
    const reasonsBefore = intakeBlock(row)?.reasons || [];
    setBusy(true);
    setErrorText('');
    try {
      const assignment = await ownRow(
        row.name, row.domain, row.targetOwner, TYPED_ROW_EVIDENCE, row.account_id, true,
      );
      // A forced write answers with the reasons it overrode; keep the door's if it has none.
      const reasons = assignment.reasons?.length ? assignment.reasons : reasonsBefore;
      setResults(current => current.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...assignment, reasons } : item
      )));
    } finally {
      setBusy(false);
    }
  }

  // The upload's requests, announced in Slack as one message once every row is in. Best effort:
  // the requests are saved already, so a failure here changes nothing the person sees.
  function announceBatch(requested: WriteActionResult[]) {
    const refs = requestedRefs(requested);
    if (mode !== 'request' || refs.length === 0) return;
    announceRequests(getToken, refs).catch(() => undefined);
  }

  // Step one is the door; an excluded row is reported and never assigned.
  async function postRow(planned: PlannedRow, requested: WriteActionResult[]): Promise<IntakeResultRow> {
    const { row, owner } = planned;
    const reply = await postIntake(getToken, {
      name: row.name,
      domain: row.domain,
      source: 'csv',
      evidence: row.evidence,
    });
    const first = reply.rows[0];
    const posted: IntakeResultRow = {
      name: row.name,
      domain: row.domain,
      outcome: first?.outcome || 'excluded',
      reason: first?.reason || '',
      written: first?.written,
      account_id: claimAccountIdFromDoor(first?.reason, first?.account_id),
      targetOwner: owner.email || undefined,
      // The door's person-gate labels. A row it stopped never reaches a writer.
      reasons: first?.reasons || [],
    };
    if (mode === 'request') {
      if (!shouldClaimAfterIntake(first?.outcome, first?.account_id)) return posted;
    } else if (!shouldAssignAfterIntake(first?.outcome, first?.account_id)) {
      return posted;
    }

    // An owner the CSV named but nobody listed matches: in the bank, flagged.
    if (owner.problem) {
      return {
        ...posted,
        assignStatus: 'unresolved',
        assignMessage: 'Not assigned — ' + owner.problem,
      };
    }
    if (!owner.email) return posted;

    const assignment = await ownRow(
      row.name, row.domain, owner.email, row.evidence,
      claimAccountIdFromDoor(first?.reason, first?.account_id),
      false, requested,
    );
    return { ...posted, ...assignment };
  }

  async function submitRows(plannedRows: PlannedRow[]) {
    setBusy(true);
    setErrorText('');
    setResults([]);
    setProgress({ done: 0, total: plannedRows.length });
    const nextResults: IntakeResultRow[] = [];
    const requested: WriteActionResult[] = [];
    try {
      // Rows post in sequence so the progress readout is honest.
      for (const planned of plannedRows) {
        if (planned.row.malformed) {
          nextResults.push({
            name: planned.row.name,
            domain: planned.row.domain,
            outcome: 'excluded',
            reason: 'malformed',
            written: false,
          });
        } else {
          nextResults.push(await postRow(planned, requested));
        }
        setProgress({ done: nextResults.length, total: plannedRows.length });
      }
      setResults(nextResults);
    } catch (error) {
      setErrorText(panelMessage(error, 'The upload stopped partway.'));
      if (nextResults.length) setResults(nextResults);
    } finally {
      // Whatever was requested before a stop is still announced.
      announceBatch(requested);
      setBusy(false);
    }
  }

  async function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const text = await file.text();
    const parsed = parseIntakeCsv(text);
    if (parsed.length === 0) {
      setPlan(null);
      setErrorText(
        'That file needs a header row with name and domain columns, and at least one row.',
      );
      return;
    }
    setErrorText('');
    setResults([]);
    setPlan({ filename: file.name, rows: parsed });
  }

  async function confirmUpload() {
    if (!plan) return;
    const plannedRows = planRows(plan.rows, defaultOwner, strategists);
    setPlan(null);
    await submitRows(plannedRows);
  }

  function clearTypedRow() {
    setName('');
    setDomain('');
    setExistingMatch(null);
  }

  async function assignExisting(row: BookAccount) {
    const accountDomain = (row.domain || '').trim();
    if (!accountDomain) {
      setErrorText(`That account has no domain to ${copy.verbButton.toLowerCase()}.`);
      return;
    }
    const alreadyInBook: IntakeResultRow = {
      name: row.account,
      domain: accountDomain,
      outcome: 'adopted',
      reason: 'already_in_book',
      written: false,
      account_id: row.id,
      targetOwner: singleOwner || undefined,
    };
    if (!singleOwner) {
      setResults([alreadyInBook]);
      clearTypedRow();
      return;
    }
    setBusy(true);
    setErrorText('');
    const requested: WriteActionResult[] = [];
    try {
      const assignment = await ownRow(
        row.account, accountDomain, singleOwner, TYPED_ROW_EVIDENCE, row.id, false, requested,
      );
      setResults([{ ...alreadyInBook, ...assignment }]);
      clearTypedRow();
    } finally {
      announceBatch(requested);
      setBusy(false);
    }
  }

  async function addTypedRow() {
    const typed: ParsedIntakeRow = {
      name: name.trim(),
      domain: domain.trim(),
      owner: '',
      evidence: TYPED_ROW_EVIDENCE,
      malformed: false,
      line_number: 0,
    };
    await submitRows(planRows([typed], singleOwner, strategists));
    clearTypedRow();
  }

  async function onAddOne(event: FormEvent) {
    event.preventDefault();
    const accountName = name.trim();
    const accountDomain = domain.trim();
    if (!accountName || !accountDomain) {
      setErrorText('Name and domain are both required.');
      return;
    }
    const match = matchingAccount(accounts, accountName, accountDomain);
    if (match) {
      setExistingMatch(match);
      setErrorText('');
      return;
    }
    await addTypedRow();
  }

  // The preview is recomputed on every render, so changing the default owner
  // after choosing a file updates the counts before anything is written.
  const planned = plan ? planRows(plan.rows, defaultOwner, strategists) : [];
  const plannedValid = planned.filter(item => !item.row.malformed);
  const plannedMalformed = planned.filter(item => item.row.malformed);
  const plannedFromCsv = plannedValid.filter(
    item => item.owner.source === 'csv' && !item.owner.problem,
  );
  const plannedFromDefault = plannedValid.filter(item => item.owner.source === 'default');
  const plannedToBank = plannedValid.filter(item => item.owner.source === 'bank');
  const plannedProblems = plannedValid.filter(item => item.owner.problem);

  const assignedCount = results.filter(isAssigned).length;
  const requestedCount = results.filter(isRequested).length;

  // A row the person gate stopped is counted once, as blocked, wherever it was stopped.
  const blockedCount = results.filter(row => intakeBlock(row)).length;
  const notAddedCount = results.filter(
    row => row.outcome === 'excluded' && !intakeBlock(row) && !row.assignStatus,
  ).length;
  const failedCount = results.filter(
    row => (row.assignStatus === 'refused' || row.assignStatus === 'unresolved') && !intakeBlock(row),
  ).length;
  const dryRunCount = results.filter(row => row.assignStatus === 'dry_run').length;
  const bankCount = results.length - assignedCount - requestedCount - blockedCount
    - notAddedCount - failedCount - dryRunCount;
  const addButtonLabel = singleOwner
    ? `${copy.verbButton} ${copy.preposition} ${singleOwnerLabel}`
    : copy.verbButton;

  // A request needs its "why"; an upload whose rows all stay in the bank requests nothing.
  const plannedRequestCount = plannedFromCsv.length + plannedFromDefault.length;
  const whyField = mode === 'request' ? (
    <label className="requestreasonfield">
      <span className="csvhint">{REQUEST_REASON_LABEL} <span className="csvtag req">Required</span></span>
      <input
        type="text"
        aria-label={REQUEST_REASON_LABEL}
        placeholder={REQUEST_REASON_PLACEHOLDER}
        maxLength={MAX_REQUEST_REASON_LENGTH}
        value={why}
        disabled={locked}
        onChange={event => setWhy(event.target.value)}
      />
    </label>
  ) : null;

  const resultBlock = results.length > 0 ? (
    <div className="csvresults">
      <div className="csvresulthead">
        <span>
          {results.length} row{results.length === 1 ? '' : 's'}
          {assignedCount ? ' · ' + assignedCount + ' assigned' : ''}
          {requestedCount
            ? ' · ' + requestedCount + ' requested, waiting for approval'
            : ''}
          {bankCount > 0 ? ' · ' + bankCount + ' left in the bank' : ''}
          {dryRunCount ? ' · ' + dryRunCount + ' not written (writes are off)' : ''}
          {failedCount ? ' · ' + failedCount + ' not ' + copy.verbPast : ''}
          {blockedCount ? ' · ' + blockedCount + ' blocked by the exclusion check' : ''}
          {notAddedCount ? ' · ' + notAddedCount + ' not added' : ''}
        </span>
        <button
          type="button"
          className="btn"
          onClick={() => {
            const csvText = buildCsv(results, INTAKE_RESULT_CSV_COLUMNS);
            downloadCsvFile('upload-results.csv', csvText);
          }}
        >
          Export results
        </button>
      </div>
      <table className="csvtable">
        <thead>
          <tr>
            <th>Account</th>
            <th>Domain</th>
            <th>Bank</th>
            <th>Owner</th>
            <th>{copy.resultColumn}</th>
          </tr>
        </thead>
        <tbody>
          {results.map((row, index) => (
            <tr key={row.domain + '-' + index}>
              <td>{row.name || '—'}</td>
              <td>{row.domain || '—'}</td>
              <td>{bankResultLabel(row)}</td>
              <td>
                {row.assignOwner ? strategistLabel(row.assignOwner, strategists) : '—'}
              </td>
              <td>
                <IntakeAssignmentCell
                  row={row}
                  mode={mode}
                  targetLabel={row.targetOwner ? strategistLabel(row.targetOwner, strategists) : ''}
                  busy={locked}
                  onForce={() => { void forceRow(index); }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null;

  return (
    <div className="csvintake">
      <section className="netnew" aria-label={copy.netNewTitle}>
        <h3 className="csvtitle">{copy.netNewTitle}</h3>
        <p className="briefempty">{copy.netNewIntro}</p>
        {previewOnly ? (
          <p className="csvnotice">You are viewing as {viewAsRole}. This is what they
            see; writing is turned off in view-as.</p>
        ) : null}
        <label className="netnewowner">
          <span className="csvhint">{copy.netNewOwnerStep}</span>
          <select
            className="csvselect"
            aria-label={copy.netNewOwnerStep}
            value={singleOwner}
            disabled={locked}
            onChange={event => setSingleOwner(event.target.value)}
          >
            {mode === 'assign' ? (
              <option value="">Choose…</option>
            ) : null}
            {singleOwnerChoices.map(choice => (
              <option key={choice.email} value={choice.email}>
                {choice.label}
              </option>
            ))}
          </select>
        </label>
        <p className="csvhint">{copy.netNewOwnerHint}</p>
        {panelOpen ? null : whyField}
        <form className="csvform" onSubmit={event => { void onAddOne(event); }}>
          <input
            type="text"
            placeholder="Name (required)"
            aria-label="Account name"
            value={name}
            disabled={locked}
            onChange={event => {
              setName(event.target.value);
              setExistingMatch(null);
            }}
          />
          <input
            type="text"
            placeholder="Domain (required)"
            aria-label="Domain"
            value={domain}
            disabled={locked}
            onChange={event => {
              setDomain(event.target.value);
              setExistingMatch(null);
            }}
          />
          <button
            type="submit"
            className="btn primary"
            disabled={locked || !singleOwner || missingWhy}
            title={missingWhy ? `${REQUEST_REASON_LABEL} is required.` : undefined}
          >
            {addButtonLabel}
          </button>
        </form>
        {existingMatch ? (
          <div className="csvconfirm" role="status">
            <p>This looks like an account already on the book.</p>
            <p className="csvconfirmacct">
              <strong>{existingMatch.account}</strong>
              {existingMatch.domain ? <span>{existingMatch.domain}</span> : null}
            </p>
            <div className="csvconfirmactions">
              {singleOwner ? (
                <button
                  type="button"
                  className="btn primary"
                  disabled={locked || missingWhy}
                  onClick={() => { void assignExisting(existingMatch); }}
                >
                  {copy.verbButton} this one {copy.preposition} {singleOwnerLabel}
                </button>
              ) : null}
              <button
                type="button"
                className="btn"
                disabled={locked || missingWhy}
                onClick={() => { void addTypedRow(); }}
              >
                No, this is a different company to add
              </button>
            </div>
          </div>
        ) : null}
        {errorText && !panelOpen ? <p className="csverr">{errorText}</p> : null}
        {!panelOpen ? resultBlock : null}
      </section>

      <button
        type="button"
        className={'btn' + (panelOpen ? ' on' : '')}
        onClick={() => setPanelOpen(current => !current)}
      >
        {copy.title}
      </button>
      {panelOpen ? (
        <div className="csvpanel">
          <h3 className="csvtitle">{copy.title}</h3>
          <p className="briefempty">{copy.intro}</p>
          {previewOnly ? (
            <p className="csvnotice">You are viewing as {viewAsRole}. This is what they
              see; uploading is turned off in view-as.</p>
          ) : null}

          <section className="csvstep">
            <div className="csvsteplabel">
              <span className="csvstepnum">1</span>
              {copy.ownerStep}
              <span className="csvtag">Optional</span>
            </div>
            <p className="csvhint">{copy.ownerHint}</p>
            <select
              className="csvselect"
              aria-label={copy.ownerStep}
              value={defaultOwner}
              disabled={locked}
              onChange={event => setDefaultOwner(event.target.value)}
            >
              {ownerChoices.map(choice => (
                <option key={choice.email || 'bank'} value={choice.email}>
                  {choice.label}
                </option>
              ))}
            </select>
            {strategistsLoading ? (
              <p className="csvhint">Loading strategists…</p>
            ) : null}
            {whyField}
          </section>

          <section className="csvstep">
            <div className="csvsteplabel">
              <span className="csvstepnum">2</span>
              Upload a CSV
            </div>
            <table className="csvcols">
              <tbody>
                <tr>
                  <td><code>name</code></td>
                  <td><span className="csvtag req">Required</span></td>
                  <td>The account name.</td>
                </tr>
                <tr>
                  <td><code>domain</code></td>
                  <td><span className="csvtag req">Required</span></td>
                  <td>The website, e.g. acme.com.</td>
                </tr>
                <tr>
                  <td><code>owner</code></td>
                  <td><span className="csvtag">Optional</span></td>
                  <td>{copy.ownerColumnHint}</td>
                </tr>
              </tbody>
            </table>
            <div className="csvfilerow">
              <label className="csvfile">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  aria-label="Choose a CSV file"
                  disabled={locked}
                  onChange={event => { void onFileChange(event); }}
                />
              </label>
              <button
                type="button"
                className="btn"
                onClick={() => downloadCsvFile(
                  'upload-template.csv', templateCsv(strategists),
                )}
              >
                Download template
              </button>
            </div>

            {plan ? (
              <div className="csvconfirm" role="status">
                <p><strong>{plan.filename}</strong> · {planned.length} row
                  {planned.length === 1 ? '' : 's'}. Nothing is written until you confirm.</p>
                <ul className="csvplan">
                  {plannedFromCsv.length ? (
                    <li>{plannedFromCsv.length} {copy.verbPast} by the owner column</li>
                  ) : null}
                  {plannedFromDefault.length ? (
                    <li>{plannedFromDefault.length} {copy.verbPast} {copy.preposition}{' '}
                      {defaultOwnerLabel} (default)</li>
                  ) : null}
                  {plannedToBank.length ? (
                    <li>{plannedToBank.length} left in the bank (no owner given)</li>
                  ) : null}
                  {plannedProblems.length ? (
                    <li className="csvbad">
                      {plannedProblems.length} with an owner that isn't a listed strategist
                      — {copy.problemFate}:{' '}
                      {plannedProblems.slice(0, 5).map(item => (
                        `${item.row.name || item.row.domain} (“${item.row.owner}”)`
                      )).join(', ')}
                      {plannedProblems.length > 5 ? ', …' : ''}
                    </li>
                  ) : null}
                  {plannedMalformed.length ? (
                    <li className="csvbad">
                      {plannedMalformed.length} missing a name or domain — skipped
                      (line{plannedMalformed.length === 1 ? '' : 's'}{' '}
                      {plannedMalformed.slice(0, 8).map(item => item.row.line_number).join(', ')}
                      {plannedMalformed.length > 8 ? ', …' : ''})
                    </li>
                  ) : null}
                </ul>
                <div className="csvconfirmactions">
                  <button
                    type="button"
                    className="btn primary"
                    disabled={locked || plannedValid.length === 0 || (missingWhy && plannedRequestCount > 0)}
                    title={missingWhy && plannedRequestCount > 0 ? `${REQUEST_REASON_LABEL} is required.` : undefined}
                    onClick={() => { void confirmUpload(); }}
                  >
                    Upload {plannedValid.length} account{plannedValid.length === 1 ? '' : 's'}
                  </button>
                  <button type="button" className="btn" onClick={() => setPlan(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          {busy && progress.total > 1 ? (
            <p className="csvhint" role="status">
              Uploading {progress.done} of {progress.total}…
            </p>
          ) : null}
          {errorText ? <p className="csverr">{errorText}</p> : null}
          {resultBlock}
        </div>
      ) : null}
    </div>
  );
}
